import { css } from '@emotion/react';
import Lottie from 'lottie-react';
import { color, spacing, typography } from '@uniquegood/realworld-studio-design';
import { onMobile } from '@/styles/responsive';
import aiSparkleAnimation from '@/assets/lottie/AI-Sparkle.json';

/**
 * DropZoneV2와 동일한 ratio 타입
 */
export type AiLoadingBoxRatio = 'fullWidth' | 'vertical' | 'square' | 'horizontal';

/**
 * DropZoneV2와 동일한 size 타입
 */
export type AiLoadingBoxSize = 'small' | 'medium';

export interface AiLoadingBoxProps {
  /**
   * 크기를 정의합니다.
   * @default "medium"
   */
  size?: AiLoadingBoxSize;

  /**
   * 가로세로 비율을 정의합니다.
   * @default "fullWidth"
   */
  ratio?: AiLoadingBoxRatio;
}

const AiGeneratingIcon = () => (
  <div css={AiIconContainer}>
    <div css={LottieGradientWrapper}>
      <Lottie
        animationData={aiSparkleAnimation}
        loop
        autoplay
        style={{
          width: '100%',
          height: '100%'
        }}
        rendererSettings={{
          preserveAspectRatio: 'xMidYMid meet',
          progressiveLoad: false,
          hideOnTransparent: true
        }}
      />
    </div>
  </div>
);

function AiLoadingBox({ size = 'medium', ratio = 'fullWidth' }: AiLoadingBoxProps) {
  return (
    <div css={[dropZoneStyle, AiLoadingBoxRatioStyle[ratio], AiLoadingBoxSizeStyle[size]]}>
      <div css={contentStyle}>
        <AiGeneratingIcon />
        {size === 'medium' && <div css={textStyle}>AI 생성 중...</div>}
      </div>
    </div>
  );
}

const dropZoneStyle = css`
  background-color: #f2f9ff;
  border: 2px dashed ${color.border_default_subdued};
  border-radius: ${spacing.borderRadius.large};
  position: relative;
  overflow: hidden;
`;

// DropZoneV2와 동일한 ratio 스타일
export const AiLoadingBoxRatioStyle = {
  fullWidth: css`
    width: 100%;
    height: 100px;
  `,
  horizontal: css`
    width: 200px;
    height: 160px;
  `,
  square: css`
    width: 160px;
    height: 160px;
  `,
  vertical: css`
    width: 160px;
    height: 200px;
  `
};

// DropZoneV2와 동일한 size 스타일
export const AiLoadingBoxSizeStyle = {
  small: css`
    width: 100px;
    height: 100px;
  `,
  medium: css``
};

const contentStyle = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: ${spacing.common.small};
`;

const AiIconContainer = css`
  width: 40px;
  height: 40px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  ${onMobile} {
    width: 32px;
    height: 32px;
  }
`;

const LottieGradientWrapper = css`
  width: 100%;
  height: 100%;
`;

const textStyle = css`
  font-feature-settings: 'liga' off, 'clig' off;
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px; /* 142.857% */
  background: var(--AiColor, linear-gradient(94deg, #4a41ff 0%, #22f8e2 100%));
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  ${onMobile} {
    font-size: ${typography.size.s1};
  }
`;

export default AiLoadingBox;
